import React, { useCallback } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../../utils/cn';

interface VideoUploaderProps {
  onFileSelect: (file: File) => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({ onFileSelect }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('video/')) {
      onFileSelect(file);
    } else {
      alert('動画ファイルをアップロードしてください。');
    }
  }, [onFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div 
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className="flex flex-col items-center w-full h-full cursor-pointer"
    >
      <div
        className={cn(
          "flex flex-col items-center justify-center p-8 rounded-lg transition-all duration-200 w-full h-full min-h-[300px]",
          isDragging 
            ? "bg-primary-100 border-2 border-dashed border-primary-500" 
            : "bg-gray-800/50 hover:bg-gray-800/60 active:bg-gray-800/70"
        )}
      >
        <div className="flex flex-col items-center justify-center p-6 rounded-lg transition-all duration-200 touch-manipulation min-h-[44px] min-w-[44px] group">
          <Upload 
            className={cn(
              "w-16 h-16 mb-4 transition-all duration-200 group-hover:scale-110",
              isDragging ? "text-primary-600" : "text-white"
            )}
          />
          <div className={cn(
            "text-xl font-medium transition-colors",
            isDragging ? "text-primary-600" : "text-white"
          )}>
            {isDragging ? 'ドロップしてアップロード' : '動画をアップロード'}
          </div>
          <div className={cn(
            "mt-2 text-sm transition-colors",
            isDragging ? "text-primary-500" : "text-gray-400"
          )}>
            クリックまたはドラッグ&ドロップで動画を選択
          </div>
          <div className="mt-4 text-xs text-gray-400">
            対応形式: MP4, MOV, WebM など
          </div>
          <div className="text-xs text-gray-400">
            最大サイズ: 500MB
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default VideoUploader;